/**
 * MSAL v5 redirect bridge, run from the app's own root page.
 *
 * MSAL v5 no longer reads a popup's or hidden iframe's URL from the main window. The page the sign-in
 * comes back to (the redirectUri) must hand the response over by calling broadcastResponseToMainFrame()
 * from @azure/msal-browser/redirect-bridge (MSAL v4 -> v5 migration guide, "redirect bridge"). Our
 * redirectUri is the app origin and stays registered as it is in Entra, so main.tsx calls this before
 * it renders anything: when the URL carries an auth response, the page acts as the bridge and the app
 * is not started in that popup / iframe. For a full-page redirect sign-in the bridge stores the
 * response in sessionStorage and returns to the page that started the sign-in, where
 * handleRedirectPromise() finishes it.
 */

/** True when the hash or query string holds an OAuth response (state plus code or error). */
export function urlHasAuthResponse(loc: Pick<Location, 'hash' | 'search'> = window.location): boolean {
  for (const part of [loc.hash, loc.search]) {
    if (!part || part.length < 2) continue;
    const params = new URLSearchParams(part.slice(1));
    if (params.has('state') && (params.has('code') || params.has('error'))) return true;
  }
  return false;
}

/**
 * Hands the auth response to the window that is waiting for it. Resolves true when MSAL took the
 * response (the popup / iframe closes, or the page navigates back to where sign-in started), false
 * when the URL was not an MSAL response, so the caller starts the app as normal.
 */
export async function runMsalRedirectBridge(): Promise<boolean> {
  try {
    const { broadcastResponseToMainFrame } = await import('@azure/msal-browser/redirect-bridge');
    await broadcastResponseToMainFrame();
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[msal] redirect bridge did not take this URL; starting the app', err);
    return false;
  }
}
